import { useTranslations } from '@vocab/react';
import { Stack, Loader, Box, Heading } from 'braid-design-system';
import { vars } from 'braid-design-system/css';
import { useRef } from 'react';

import type { Role } from 'src/__generated__/graphql';
import { CareerHistoryEditor } from 'src/components/CareerHistory/CareerHistoryEditor';
import { CareerHistoryList } from 'src/components/CareerHistory/CareerHistoryList';
import { FirstRoleForm } from 'src/components/CareerHistory/FirstRoleForm';
import {
  EditorDrawer,
  EditorDrawerProvider,
} from 'src/ui/EditorDrawer/EditorDrawer';

import translations from './.vocab';
import { useCareerHistoryData } from './hooks/useCareerHistoryData';

interface Props {
  isNewToWorkforce: boolean;
  onNewToWorkforceToggle: (toggle: boolean) => void;
  unconfirmedRoles?: Role[];
}
export function CareerHistory({
  isNewToWorkforce = false,
  onNewToWorkforceToggle,
  unconfirmedRoles = [],
}: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslations(translations);
  const { roles, loading } = useCareerHistoryData();

  const allRoles = [...unconfirmedRoles, ...roles];

  if (loading) {
    return <Loader />;
  }

  return (
    <Box
      ref={scrollRef}
      data={{ automation: 'career-history-section' }}
      style={{ scrollMarginTop: vars.space.xxlarge }} // offset for the Stack wrapping this component
    >
      <EditorDrawerProvider name="career-history">
        <Stack space="large">
          <Heading level="3">{t('Career history')}</Heading>
          {roles.length === 0 && (
            <FirstRoleForm
              isNewToWorkforce={isNewToWorkforce}
              onNewToWorkforceToggle={onNewToWorkforceToggle}
              roles={roles}
              unconfirmedRoles={unconfirmedRoles}
            />
          )}
          {allRoles.length > 0 && !isNewToWorkforce && (
            <>
              <CareerHistoryList
                unconfirmedRoles={unconfirmedRoles}
                roles={roles}
              />
              <EditorDrawer
                title={t('Career history')}
                name="career-history"
                onClose={() => {
                  scrollRef.current?.scrollIntoView?.({
                    behavior: 'smooth',
                    block: 'start',
                  });
                }}
              >
                {allRoles.length > 0 && (
                  <CareerHistoryEditor
                    unconfirmedRoles={unconfirmedRoles}
                    roles={roles}
                    shouldCloseDrawerOnExit
                  />
                )}
              </EditorDrawer>
            </>
          )}
        </Stack>
      </EditorDrawerProvider>
    </Box>
  );
}
